import React, { useEffect, useRef, useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import Modal      from "@react/shared/ui/Modal"
import Button     from "@react/shared/ui/Button"
import IconButton from "@react/shared/ui/IconButton"
import { fetchTaskPhotos, uploadTaskPhoto, destroyTaskPhoto } from "../api/task_photos"
import { dateFmt } from "../helpers/format"

const todayISO = () => new Date().toISOString().slice(0, 10)

const TaskPhotosModal = ({ isOpen, projectId, task, onClose, onCountChange }) => {
  const [photos,  setPhotos]  = useState([])
  const [loading, setLoading] = useState(false)
  const [caption, setCaption] = useState("")
  const [takenAt, setTakenAt] = useState(todayISO())
  const [uploading, setUploading] = useState(false)
  const [errors,  setErrors]  = useState([])
  const [lightbox, setLightbox] = useState(null)
  const fileInputRef = useRef(null)

  useEffect(() => {
    if (!isOpen || !task) return
    setErrors([])
    setLoading(true)
    fetchTaskPhotos(projectId, task.id)
      .then(data => setPhotos(data?.items || []))
      .catch(() => setErrors([I18n.t("projects.task_photos.errors.network")]))
      .finally(() => setLoading(false))
  }, [isOpen, projectId, task])

  const notifyCount = (next) => {
    if (onCountChange) onCountChange(task.id, next.length)
  }

  const handleFiles = (files) => {
    if (!files || files.length === 0) return
    setErrors([])
    setUploading(true)

    const uploads = Array.from(files).map(file =>
      uploadTaskPhoto(projectId, task.id, file, caption, takenAt).then(response => {
        if (response?.errors) return { error: response.errors }
        return response
      })
    )

    Promise.all(uploads).then(results => {
      const ok      = results.filter(r => !r.error)
      const failed  = results.filter(r => r.error)
      if (failed.length > 0) {
        setErrors([failed[0].error].flat())
      }
      if (ok.length > 0) {
        const next = [...ok, ...photos]
        setPhotos(next)
        notifyCount(next)
        setCaption("")
      }
      setUploading(false)
      if (fileInputRef.current) fileInputRef.current.value = ""
    }).catch(() => {
      setErrors([I18n.t("projects.task_photos.errors.network")])
      setUploading(false)
    })
  }

  const handleDelete = (photo) => {
    if (!confirm(I18n.t("projects.task_photos.actions.confirm_destroy"))) return
    destroyTaskPhoto(projectId, task.id, photo.id).then(response => {
      if (response?.success) {
        const next = photos.filter(p => p.id !== photo.id)
        setPhotos(next)
        notifyCount(next)
      }
    })
  }

  const footer = (
    <>
      <div className="text-[12px] text-slate-500">
        {photos.length > 0 && I18n.t("projects.task_photos.count", { count: photos.length })}
      </div>
      <Button variant="ghost" onClick={onClose}>{I18n.t("projects.task_photos.close")}</Button>
    </>
  )

  return (
    <Modal
      isOpen  = {isOpen}
      onClose = {onClose}
      eyebrow = {I18n.t("projects.task_photos.eyebrow")}
      title   = {task?.title || I18n.t("projects.task_photos.title_fallback")}
      footer  = {footer}
    >
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-5">
        <div className="text-[10.5px] uppercase tracking-wider font-semibold text-slate-500 mb-2">
          {I18n.t("projects.task_photos.upload_eyebrow")}
        </div>
        <div className="grid grid-cols-12 gap-3 items-end">
          <div className="col-span-6">
            <label className="block text-xs font-medium text-slate-700 mb-1.5">
              {I18n.t("projects.task_photos.caption")}
            </label>
            <input
              value     = {caption}
              onChange  = {e => setCaption(e.target.value)}
              placeholder = {I18n.t("projects.task_photos.caption_placeholder")}
              className = "w-full h-9 px-3 rounded-md border border-slate-200 text-sm"
            />
          </div>
          <div className="col-span-3">
            <label className="block text-xs font-medium text-slate-700 mb-1.5">
              {I18n.t("projects.task_photos.taken_at")}
            </label>
            <input
              type      = "date"
              value     = {takenAt}
              onChange  = {e => setTakenAt(e.target.value)}
              className = "w-full h-9 px-3 rounded-md border border-slate-200 text-sm"
            />
          </div>
          <div className="col-span-3">
            <Button
              variant   = "primary"
              size      = "sm"
              disabled  = {uploading}
              onClick   = {() => fileInputRef.current?.click()}
              className = "w-full justify-center"
            >
              <FontAwesomeIcon icon={uploading ? "spinner" : "upload"} spin={uploading} />
              {I18n.t("projects.task_photos.upload_cta")}
            </Button>
            <input
              ref      = {fileInputRef}
              type     = "file"
              accept   = "image/*"
              multiple
              onChange = {e => handleFiles(e.target.files)}
              className= "hidden"
            />
          </div>
        </div>
        {task && (
          <p className="mt-2 text-[11.5px] text-slate-500">
            {I18n.t("projects.task_photos.recorded_completion_hint", { pct: Math.round(task.completion || 0) })}
          </p>
        )}
      </div>

      {errors.length > 0 && (
        <div className="mb-4 px-3 py-2 rounded-md bg-rose-50 border border-rose-200 text-rose-700 text-sm">
          {errors.join(", ")}
        </div>
      )}

      {loading ? (
        <div className="py-12 text-center text-sm text-slate-500">
          <FontAwesomeIcon icon="spinner" spin /> {I18n.t("projects.task_photos.loading")}
        </div>
      ) : photos.length === 0 ? (
        <div className="py-12 text-center text-[13px] text-slate-500">
          {I18n.t("projects.task_photos.empty")}
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {photos.map(photo => (
            <div key={photo.id} className="group relative bg-white border border-slate-200 rounded-lg overflow-hidden">
              <button
                type      = "button"
                onClick   = {() => setLightbox(photo)}
                className = "block w-full aspect-[4/3] bg-slate-100 overflow-hidden"
              >
                {photo.image_url
                  ? <img src={photo.image_url} alt={photo.caption || ""} className="w-full h-full object-cover" />
                  : <div className="w-full h-full grid place-items-center text-slate-400">
                      <FontAwesomeIcon icon="image" className="text-2xl" />
                    </div>}
              </button>
              <div className="p-2.5">
                {photo.caption && (
                  <div className="text-[12.5px] text-slate-900 truncate" title={photo.caption}>{photo.caption}</div>
                )}
                <div className="text-[10.5px] text-slate-500 flex items-center gap-2 mt-1">
                  {photo.taken_at && <span>{dateFmt(photo.taken_at)}</span>}
                  {photo.recorded_completion > 0 && (
                    <span className="px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded font-medium">
                      {Math.round(photo.recorded_completion)} %
                    </span>
                  )}
                </div>
              </div>
              <div className="absolute top-1.5 right-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                <IconButton tone="danger" size="sm" onClick={() => handleDelete(photo)} title={I18n.t("projects.task_photos.actions.destroy")}>
                  <FontAwesomeIcon icon="trash" />
                </IconButton>
              </div>
            </div>
          ))}
        </div>
      )}

      {lightbox && (
        <div
          className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-8"
          onClick={() => setLightbox(null)}
        >
          <img src={lightbox.image_url} alt={lightbox.caption || ""} className="max-w-full max-h-full object-contain" />
        </div>
      )}
    </Modal>
  )
}

export default TaskPhotosModal
